import { useEffect, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Volume2 } from 'lucide-react';
import { api, type Page, type Schema } from '../api/client';
import type { StudioTask } from './production';
import { isVoiceModel } from './modelKinds';
import { VoicePicker } from './Speech';
import { useStudio } from './App';
import { Field, Spinner, useApi } from './shared';

export type VoiceProfile = {
  language: string;
  timbre: string;
  pace: string;
  delivery: string;
  synthesis?: { voiceId: string; language: string };
};
export type CharacterVoice = {
  characterId: string;
  name: string;
  revision: string;
  hash: string;
  source: 'confirmed' | 'character_profile' | 'default';
  profile: VoiceProfile;
};
export function useCharacterVoices(seriesId?: string, branchId?: string) {
  const { base } = useStudio();
  return useApi<Page<CharacterVoice>>(
    `${base}/series/${seriesId}/voices?branchId=${branchId}`,
    !!seriesId && !!branchId,
  );
}
export function voiceSummary(voice: CharacterVoice) {
  const p = voice.profile;
  return `${p.language} · ${p.timbre} · ${p.pace}。${p.delivery}${p.synthesis ? ` · 独立配音音色：${p.synthesis.voiceId}` : ''}`;
}
export function CharacterVoicePanel({
  characterId,
  branchId: chosenBranch,
}: {
  characterId: string;
  branchId?: string;
}) {
  const { base, project, branch } = useStudio();
  const branchId = chosenBranch ?? branch?.id;
  const query = useCharacterVoices(project?.id, branchId);
  const voice = query.data?.items.find((v) => v.characterId === characterId);
  const [profile, setProfile] = useState<VoiceProfile | null>(null);
  const [revision, setRevision] = useState('0');
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState('');
  const [error, setError] = useState('');
  const cache = useQueryClient();
  const models = useApi<Page<Schema<'ModelSummary'>>>(`${base}/models`);
  const options = models.data?.items.filter(isVoiceModel) ?? [];
  const [modelId, setModelId] = useState('');
  const [sampleText, setSampleText] = useState(
    '你终于来了。我一直在这里等你，我们一起把这个故事讲完。',
  );
  const previewPath = `${base}/studio-tasks?seriesId=${project?.id}&branchId=${branchId}&characterId=${characterId}`;
  const samples = useQuery({
    queryKey: ['api', previewPath],
    queryFn: () => api<Page<StudioTask>>(previewPath),
    enabled: !!project?.id && !!branchId,
    refetchInterval: (q) =>
      q.state.data?.items.some(
        (task) => task.kind === 'voice' && ['queued', 'running'].includes(task.state),
      )
        ? 2000
        : false,
  });
  const previewTasks =
    samples.data?.items.filter((task) => task.kind === 'voice').slice(0, 3) ?? [];
  const generating = previewTasks.some((task) => ['queued', 'running'].includes(task.state));
  useEffect(() => {
    if (voice) {
      setProfile({ ...voice.profile });
      setRevision(voice.revision);
    }
  }, [voice?.hash]);
  return (
    <section className="character-voice-panel" aria-label="角色声音设定">
      <strong>
        <Volume2 size={17} /> 角色声音
      </strong>
      <p className="quiet">视频原声按以下描述表演；独立配音使用你为角色确认的音色。</p>
      {query.isPending && <Spinner />}
      {profile && (
        <>
          <div className="form-grid">
            {(
              [
                ['language', '语言 / 口音', '如：普通话，轻微北方口音'],
                ['timbre', '声线与音色', '如：青年男声，清亮、略带沙哑'],
                ['pace', '语速与节奏', '如：语速偏慢，句间有自然停顿'],
                ['delivery', '说话习惯', '如：话少、克制，强调关键词；情绪随场景变化'],
              ] as const
            ).map(([key, label, placeholder]) => (
              <Field label={label} key={key}>
                <textarea
                  rows={key === 'delivery' ? 3 : 2}
                  value={profile[key]}
                  maxLength={key === 'delivery' ? 600 : 180}
                  placeholder={placeholder}
                  disabled={busy}
                  onChange={(e) => {
                    setProfile({ ...profile, [key]: e.target.value });
                    setNotice('');
                  }}
                />
              </Field>
            ))}
          </div>
          <section className="speech-settings" aria-label="角色独立配音">
            <label className="novel-check">
              <input
                type="checkbox"
                checked={!!profile.synthesis}
                disabled={busy}
                onChange={(e) => {
                  const next = { ...profile };
                  if (e.target.checked) next.synthesis = { voiceId: 'eve', language: 'zh' };
                  else delete next.synthesis;
                  setProfile(next);
                  setNotice('');
                }}
              />
              为此角色绑定独立配音音色
            </label>
            {profile.synthesis && (
              <>
                <VoicePicker
                  value={profile.synthesis.voiceId}
                  disabled={busy}
                  change={(voiceId) => {
                    setProfile({ ...profile, synthesis: { ...profile.synthesis!, voiceId } });
                    setNotice('');
                  }}
                />
                <Field
                  label="试听模型"
                  hint="音色随角色保存；正式制作时使用流程中统一选择的配音模型。"
                >
                  <select
                    value={modelId || options[0]?.catalogModelId || ''}
                    disabled={busy}
                    onChange={(e) => setModelId(e.target.value)}
                  >
                    {!options.length && <option value="">暂无语音模型，请先配置模型连接</option>}
                    {options.map((model) => (
                      <option key={model.catalogModelId} value={model.catalogModelId}>
                        {model.displayName}
                      </option>
                    ))}
                  </select>
                </Field>
                <Field label="中文试听台词">
                  <textarea
                    value={sampleText}
                    rows={2}
                    maxLength={500}
                    disabled={busy}
                    onChange={(e) => setSampleText(e.target.value)}
                  />
                </Field>
                <button
                  className="button secondary"
                  type="button"
                  disabled={
                    busy ||
                    generating ||
                    !sampleText.trim() ||
                    !profile.synthesis.voiceId.trim() ||
                    !options.length
                  }
                  onClick={() => {
                    setBusy(true);
                    setError('');
                    setNotice('');
                    void api(`${base}/studio-tasks`, 'POST', {
                      kind: 'voice',
                      seriesId: project?.id,
                      branchId,
                      characterId,
                      modelId: modelId || options[0]?.catalogModelId,
                      text: sampleText,
                      voice: profile.synthesis!.voiceId.trim(),
                      language: profile.synthesis!.language,
                      confirmed: true,
                    })
                      .then(async () => {
                        await samples.refetch();
                        setNotice('试听任务已提交，结果会自动保存。试听满意后确认保存角色声音。');
                      })
                      .catch((e: Error) => setError(e.message))
                      .finally(() => setBusy(false));
                  }}
                >
                  {generating ? <Spinner /> : <Volume2 size={16} />}生成中文试听
                </button>
              </>
            )}
            {previewTasks.map((task) => (
              <div className="speech-sample" key={task.id}>
                <small>
                  {new Date(task.createdAt).toLocaleString()} ·{' '}
                  {['queued', 'running'].includes(task.state)
                    ? '正在生成试听'
                    : task.state === 'completed'
                      ? '已保存的试听'
                      : '试听未完成'}
                </small>
                {task.speechPreview ? (
                  <>
                    <strong>
                      {task.speechPreview.voice} ·{' '}
                      {task.speechPreview.modelName ||
                        models.data?.items.find((model) => model.catalogModelId === task.modelId)
                          ?.displayName ||
                        '语音模型'}
                    </strong>
                    <p className="quiet">语言：{task.speechPreview.language}</p>
                    <p>{task.speechPreview.text}</p>
                    <button
                      type="button"
                      className="button small secondary"
                      disabled={busy || !task.result.asset?.url}
                      onClick={() => {
                        setProfile({
                          ...profile,
                          synthesis: {
                            voiceId: task.speechPreview!.voice,
                            language: task.speechPreview!.language,
                          },
                        });
                        if (task.modelId) setModelId(task.modelId);
                        setNotice('已选用这次试听的音色，请确认并保存角色声音。');
                      }}
                    >
                      使用此音色
                    </button>
                  </>
                ) : (
                  <p className="quiet">旧记录未保留可展示的试听参数，请重新试听后选择音色。</p>
                )}
                {task.result.asset?.url && (
                  <audio
                    controls
                    preload="metadata"
                    src={task.result.asset.url}
                    aria-label={`${voice?.name ?? '角色'}声音试听`}
                  />
                )}
                {task.errorMessage && <p className="form-error">{task.errorMessage}</p>}
              </div>
            ))}
            {samples.error && <p className="form-error">{samples.error.message}</p>}
          </section>
          <button
            type="button"
            className="button secondary"
            disabled={
              busy ||
              [profile.language, profile.timbre, profile.pace, profile.delivery].some(
                (v) => !v.trim(),
              ) ||
              (!!profile.synthesis && !profile.synthesis.voiceId.trim())
            }
            onClick={() => {
              setBusy(true);
              setError('');
              setNotice('');
              void api<CharacterVoice>(
                `${base}/series/${project?.id}/characters/${characterId}/voice?branchId=${branchId}`,
                'PUT',
                {
                  expectedRevision: revision,
                  profile: profile.synthesis
                    ? {
                        ...profile,
                        synthesis: {
                          ...profile.synthesis,
                          voiceId: profile.synthesis.voiceId.trim(),
                        },
                      }
                    : profile,
                  humanReviewed: true,
                },
              )
                .then(async () => {
                  await cache.invalidateQueries({
                    queryKey: ['api', `${base}/series/${project?.id}/voices?branchId=${branchId}`],
                  });
                  setNotice('角色声音已保存。后续新生成的镜头与独立配音会继承相应设定。');
                })
                .catch((e: Error) => setError(e.message))
                .finally(() => setBusy(false));
            }}
          >
            {busy ? <Spinner /> : <Volume2 size={16} />}确认并保存角色声音
          </button>
        </>
      )}
      {(error || query.error) && (
        <p className="form-error" role="alert">
          {error || query.error?.message}
        </p>
      )}
      {notice && <p role="status">{notice}</p>}
      <p className="quiet">
        绑定音色不会改变原有视频原声模式。独立配音需在制作流程中选择；它会替换视频原声，不会自动调整人物口型。
      </p>
    </section>
  );
}
